/* Asymétries — Positionnement de valeur (Constante Partners / Noam)
 *
 * 3 onglets : Contacts · Organisations · Logiques métiers.
 * Sur chaque onglet :
 *   - Scatter 2D (Centralité × Expertise)
 *   - Courbe de Gauss avec marqueur "Vous êtes ici" + percentile
 *   - Tableau des relations en zone grise (priorité à monter en gamme)
 *
 * Doctrine : si on est dans les queues (top 5 % ou bottom 5 %), peu d'acteurs
 * du marché peuvent comprendre nos prestations — c'est précisément la zone
 * de rareté économique.
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { useTranslation } from "react-i18next";
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine, BarChart, Bar, Legend,
  Cell, ComposedChart, Line,
} from "recharts";
import {
  Crown, Sparkles, AlertTriangle, Loader2, Target, Users, Building2, Network,
  Star, X, Lightbulb, ExternalLink, TrendingUp,
} from "lucide-react";
import AIModeToggle from "@/components/AIModeToggle";
import DynamicReadingPanel from "@/components/DynamicReadingPanel";
import InlineGuide from "@/components/InlineGuide";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";
import AsymetriesMultiSourcePanel from "@/components/AsymetriesMultiSourcePanel";
import DispersionMarche from "./asymetries/DispersionMarche";
import DispersionClientele from "./asymetries/DispersionClientele";

const ZONE_META = {
  pivot:    { color: "#EF9F27", label: "Pivot",     desc: "Position centrale — capte le flux" },
  expert:   { color: "#1D9E75", label: "Expert",    desc: "Problème rare difficile à déléguer" },
  emerging: { color: "#378ADD", label: "Émergent",  desc: "En cours de positionnement" },
  gray:     { color: "#71717A", label: "Zone grise", desc: "Substituable — marge faible" },
};

const TABS = [
  { key: "contacts",      label: "Contacts",       icon: Users,      desc: "Personnes individuelles" },
  { key: "organisations", label: "Organisations",  icon: Building2,  desc: "Sociétés · holdings · deals · mandats" },
  { key: "logiques",      label: "Logiques métiers", icon: Network,  desc: "Chaînages causaux" },
  { key: "marche",        label: "Dispersion marché", icon: Target,  desc: "Vos pairs — même métier, même périmètre", custom: true },
  { key: "clientele",     label: "Dispersion clientèle", icon: TrendingUp, desc: "Votre mix d'offres et migrations", custom: true },
];

function rarityBadgeText(self) {
  if (!self) return null;
  const p = self.percentile;
  if (p >= 95 || p <= 5) return { color: "#EF9F27", text: "RARETÉ EXTRÊME" };
  if (p >= 80 || p <= 20) return { color: "#1D9E75", text: "RARETÉ ÉLEVÉE" };
  return { color: "#71717A", text: "POSITION MÉDIANE" };
}

export default function Asymetries() {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("organisations");
  // P0 chantier 1 — lecture IA stratégique
  const [aiBusy, setAiBusy] = useState(false);
  const [dynamicOpen, setDynamicOpen] = useState(false); // V23.14.7c — Asymétries Dynamiques
  const [aiProgress, setAiProgress] = useState("");
  const [aiReading, setAiReading] = useState("");
  const [aiCategory, setAiCategory] = useState(null);
  const [aiMode, setAiMode] = useState("internal");
  const [aiSources, setAiSources] = useState([]);

  const runAiReading = async (categoryKey) => {
    setAiBusy(true);
    setAiProgress("Démarrage de l'analyse…");
    try {
      // V23.14.3 — Pattern job-based (résout Cloudflare overload)
      const params = { mode: aiMode };
      if (categoryKey) params.category = categoryKey;
      const start = await api.post("/workspace/asymetries/ai-reading/start", null, { params });
      const jobId = start.data?.job_id;
      if (!jobId) throw new Error("Job ID manquant");

      // Polling toutes les 2s jusqu'à done|failed
      const result = await new Promise((resolve, reject) => {
        const tick = async () => {
          try {
            const j = await api.get(`/workspace/asymetries/ai-reading/jobs/${jobId}`);
            const d = j.data || {};
            setAiProgress(d.progress || "");
            if (d.status === "done") {
              clearInterval(timer);
              resolve(d.result || {});
            } else if (d.status === "failed") {
              clearInterval(timer);
              reject(new Error(d.error || "Analyse échouée"));
            }
          } catch (e) {
            clearInterval(timer);
            reject(e);
          }
        };
        const timer = setInterval(tick, 2000);
        tick();
      });

      setAiReading(result?.ai_reading || "");
      setAiCategory(result?.category || "global");
      setAiSources(result?.web_sources || []);
    } catch (err) {
      notify.error(fmtError(err, "Lecture IA indisponible."));
    } finally {
      setAiBusy(false);
      setAiProgress("");
    }
  };

  useEffect(() => {
    setLoading(true);
    api.get("/workspace/asymetries")
      .then((r) => setData(r.data))
      .catch((e) => notify.error(fmtError(e, "Chargement impossible")))
      .finally(() => setLoading(false));
  }, []);

  const group = data?.groups?.[activeTab];
  const items = group?.items || [];
  const stats = group?.stats || { total: 0, zones: { pivot: 0, expert: 0, emerging: 0, gray: 0 }, gray_ratio: 0 };
  const self = group?.self || null;
  const rarity = rarityBadgeText(self);

  /* Scatter par zone */
  const scatterByZone = useMemo(() => {
    const out = { pivot: [], expert: [], emerging: [], gray: [] };
    for (const it of items) {
      out[it.zone].push({
        x: it.centrality,
        y: it.expertise,
        z: 100 - it.substituability,
        name: it.name,
        sub: it.subtitle,
        pos: it.value_position,
      });
    }
    return out;
  }, [items]);

  /* Marqueur "Vous êtes ici" sur le scatter — moyenne pondérée des top 25 % */
  const selfDot = useMemo(() => {
    if (!items.length) return [];
    const sorted = [...items].sort((a, b) => b.value_position - a.value_position);
    const top = sorted.slice(0, Math.max(1, Math.floor(sorted.length / 4)));
    const cen = top.reduce((s, x) => s + x.centrality, 0) / top.length;
    const exp = top.reduce((s, x) => s + x.expertise, 0) / top.length;
    return [{ x: cen, y: exp, z: 300, name: "Votre cabinet", sub: `position ${self?.value_position ?? 0}`, pos: self?.value_position }];
  }, [items, self]);

  /* Histogramme + courbe normale théorique (overlay) */
  const histWithGauss = useMemo(() => {
    if (!group) return [];
    const mu = group.stats.mean;
    const sigma = group.stats.stddev || 1;
    const total = group.stats.total;
    const binWidth = 10;
    return group.histogram.map((h) => {
      const z = (h.x - mu) / sigma;
      const pdf = Math.exp(-(z * z) / 2) / (Math.sqrt(2 * Math.PI) * sigma);
      return { ...h, gauss: Math.round(pdf * total * binWidth * 10) / 10 };
    });
  }, [group]);

  const grayList = useMemo(() => {
    return items.filter((it) => it.zone === "gray")
      .sort((a, b) => b.substituability - a.substituability)
      .slice(0, 12);
  }, [items]);

  if (loading) {
    return (
      <div className="p-10 text-zinc-500 text-sm flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="asymetries-root">
      <ThreeQPageHeader endpoint="/workspace/asymetries/top-priority" />
      {/* V23.15.42 — Triangulation multi-source (Annuaire + Internet + auto-observation) */}
      <AsymetriesMultiSourcePanel />
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="noam-overline">Doctrine — capture de valeur dans le graphe</div>
          <h1 className="noam-display text-2xl font-bold tracking-tight">Asymétries</h1>
          <p className="text-zinc-400 text-sm mt-1 max-w-3xl leading-relaxed">
            Chaque relation est positionnée sur deux axes : <span className="text-[#EF9F27] font-semibold">centralité</span> (proximité du flux) et <span className="text-[#1D9E75] font-semibold">expertise</span> (rareté du problème résolu). Les <span className="text-[#EF9F27] font-semibold">queues de Gauss</span> sont la zone de rareté économique — peu d'acteurs y comprennent vos prestations, mais ceux qui les comprennent les paient au juste prix.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <AIModeToggle value={aiMode} onChange={setAiMode} />
          <button
            type="button"
            onClick={() => setDynamicOpen(true)}
            className="btn-secondary text-xs flex-shrink-0"
            data-testid="asym-dyn-open-btn"
            title="Lecture vivante : croise votre profil + concurrents web + courbe de Gauss pour vous aider à mieux vous positionner."
          >
            <TrendingUp className="w-3.5 h-3.5 text-[#7F77DD]" /> Lecture dynamique
          </button>
          <button
            type="button"
            onClick={() => runAiReading(activeTab)}
            disabled={aiBusy || !data}
            className="btn-primary text-xs flex-shrink-0 disabled:opacity-50"
            data-testid="asym-ai-reading-btn"
          >
            {aiBusy
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {aiProgress || "Analyse…"}</>
              : <><Lightbulb className="w-3.5 h-3.5" /> Comprendre ma position</>
            }
          </button>
        </div>
      </div>

      <InlineGuide
        id="asymetries-intro"
        title="Ici, vous lisez votre position concurrentielle"
        bullets={[
          "Visualiser vos relations sur deux axes : centralité (proximité du flux) et expertise (rareté du problème résolu).",
          "Cliquer « Comprendre ma position » pour une lecture IA honnête de votre place dans le marché.",
          "Activer « Lecture dynamique » pour comparer votre profil à des concurrents identifiés sur le web (Tavily + NOAM).",
        ]}
        hint="Les queues de Gauss (extrémités) sont la zone de rareté économique où la marge est la plus haute."
      />

      {/* TABS */}
      <div className="flex gap-1 border-b border-white/10" data-testid="asym-tabs">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          const tabStats = data?.groups?.[tab.key]?.stats;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative px-4 py-2.5 text-xs flex items-center gap-2 border-b-2 transition-colors ${
                isActive ? "border-[#378ADD] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}
              data-testid={`asym-tab-${tab.key}`}
            >
              <Icon className="w-3.5 h-3.5" />
              <span className="font-semibold">{tab.label}</span>
              {tabStats && (
                <span className="text-[10px] text-zinc-500 font-mono ml-1">({tabStats.total})</span>
              )}
            </button>
          );
        })}
      </div>

      {activeTab === "marche" ? (
        <DispersionMarche />
      ) : activeTab === "clientele" ? (
        <DispersionClientele />
      ) : stats.total === 0 ? (
        <div className="noam-card p-10 text-center text-zinc-500 text-sm">
          Aucune donnée dans cette catégorie. Importez vos {TABS.find((t) => t.key === activeTab)?.label.toLowerCase()} pour générer la cartographie.
        </div>
      ) : (
        <>
          {/* KPIs incluant la position du cabinet */}
          <div className="grid md:grid-cols-5 gap-3" data-testid="asym-kpis">
            <Kpi label={`Total ${TABS.find((t) => t.key === activeTab).label.toLowerCase()}`} value={stats.total} />
            <Kpi label="Pivots" value={stats.zones.pivot} accent={ZONE_META.pivot.color} icon={Crown} />
            <Kpi label="Experts" value={stats.zones.expert} accent={ZONE_META.expert.color} icon={Target} />
            <Kpi label="Zone grise" value={`${stats.gray_ratio}%`} accent={ZONE_META.gray.color} icon={AlertTriangle} hint={`${stats.zones.gray} relations`} />
            {self && (
              <div className="noam-card p-4 border" style={{ borderColor: `${rarity.color}55` }} data-testid="asym-self-kpi">
                <div className="flex items-start justify-between">
                  <div className="noam-overline">Votre position</div>
                  <Star className="w-3.5 h-3.5" style={{ color: rarity.color }} />
                </div>
                <div className="noam-display text-3xl font-bold mt-1" style={{ color: rarity.color }}>{self.percentile}<span className="text-base">e</span></div>
                <div className="text-[10px] uppercase tracking-wider mt-1 font-semibold" style={{ color: rarity.color }}>
                  {rarity.text}
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  Médiane marché : {self.median_market}
                </div>
              </div>
            )}
          </div>

          {/* Scatter + Gauss côte à côte */}
          <div className="grid lg:grid-cols-5 gap-4">
            {/* Scatter */}
            <div className="lg:col-span-3 noam-card p-4" data-testid="asym-scatter-card">
              <div className="noam-overline">Cartographie</div>
              <div className="text-sm font-semibold text-zinc-200">Centralité × Expertise</div>
              <div style={{ width: "100%", height: 380 }} className="mt-2">
                <ResponsiveContainer>
                  <ScatterChart margin={{ top: 10, right: 12, bottom: 40, left: 12 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" />
                    <XAxis
                      type="number" dataKey="x" name="Centralité" domain={[0, 100]}
                      tick={{ fill: "#71717A", fontSize: 10 }}
                      label={{ value: "Centralité (proximité du flux)", position: "insideBottom", offset: -18, fill: "#71717A", fontSize: 11 }}
                    />
                    <YAxis
                      type="number" dataKey="y" name="Expertise" domain={[0, 100]}
                      tick={{ fill: "#71717A", fontSize: 10 }}
                      label={{ value: "Expertise", angle: -90, position: "insideLeft", fill: "#71717A", fontSize: 11 }}
                    />
                    <ZAxis type="number" dataKey="z" range={[40, 320]} name="Rareté" />
                    <Tooltip
                      cursor={{ strokeDasharray: "3 3", stroke: "#378ADD66" }}
                      contentStyle={{ background: "#0E0E10", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, fontSize: 11 }}
                      formatter={(v, k, p) => {
                        if (k === "name") return [p.payload?.name, "Relation"];
                        return [v, k];
                      }}
                      labelFormatter={() => ""}
                    />
                    <ReferenceLine x={30} stroke="#EF9F2755" strokeDasharray="2 4" />
                    <ReferenceLine y={30} stroke="#1D9E7555" strokeDasharray="2 4" />
                    {["pivot", "expert", "emerging", "gray"].map((z) => (
                      <Scatter key={z} name={ZONE_META[z].label} data={scatterByZone[z]}
                        fill={ZONE_META[z].color} fillOpacity={0.7}
                        stroke={ZONE_META[z].color} strokeWidth={0.5} />
                    ))}
                    {/* Marqueur "votre cabinet" */}
                    <Scatter name="★ Votre cabinet" data={selfDot}
                      fill="#FACC15" fillOpacity={0.9}
                      stroke="#FACC15" strokeWidth={2} shape="star" />
                    <Legend wrapperStyle={{ fontSize: 10, paddingTop: 14 }} iconSize={8} />
                  </ScatterChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Gauss avec marqueur "Vous êtes ici" */}
            <div className="lg:col-span-2 noam-card p-4" data-testid="asym-gauss-card">
              <div className="noam-overline">Distribution (Gauss)</div>
              <div className="text-sm font-semibold text-zinc-200">Position de valeur</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                μ = {stats.mean} · σ = {stats.stddev}
              </div>
              <div style={{ width: "100%", height: 280 }} className="mt-3">
                <ResponsiveContainer>
                  <ComposedChart data={histWithGauss} margin={{ top: 8, right: 8, bottom: 28, left: -8 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.05)" vertical={false} />
                    <XAxis dataKey="x" tick={{ fill: "#71717A", fontSize: 10 }}
                      label={{ value: "Score de position", position: "insideBottom", offset: -12, fill: "#71717A", fontSize: 10 }} />
                    <YAxis tick={{ fill: "#71717A", fontSize: 10 }} />
                    <Tooltip contentStyle={{ background: "#0E0E10", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 6, fontSize: 11 }}
                      formatter={(v, k) => [v, k === "count" ? "Observés" : k === "gauss" ? "Loi normale" : k]} />
                    <ReferenceLine x={0} stroke="#71717A" strokeDasharray="2 4" />
                    {self && (
                      <ReferenceLine
                        x={self.value_position}
                        stroke="#FACC15"
                        strokeWidth={2}
                        label={{ value: `★ Vous · p${self.percentile}`, fill: "#FACC15", fontSize: 10, position: "top" }}
                        data-testid="asym-gauss-self-line"
                      />
                    )}
                    <Bar dataKey="count" name="Observés" radius={[2, 2, 0, 0]}>
                      {histWithGauss.map((entry, i) => (
                        <Cell key={i} fill={entry.x < 0 ? ZONE_META.gray.color : entry.x < 30 ? ZONE_META.emerging.color : ZONE_META.pivot.color} fillOpacity={0.7} />
                      ))}
                    </Bar>
                    <Line type="monotone" dataKey="gauss" name="Loi normale" stroke="#378ADD" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
              {self && (
                <div className="text-[10px] text-zinc-400 mt-2 leading-relaxed">
                  Votre cabinet se situe au <span style={{ color: rarity.color }} className="font-bold">{self.percentile}<sup>e</sup> percentile</span> de cette distribution.
                  {self.rarity_band === "extreme" && " Position de rareté extrême — peu d'acteurs du marché peuvent comprendre vos prestations."}
                  {self.rarity_band === "high" && " Position de rareté élevée — au-dessus de la médiane du marché."}
                  {self.rarity_band === "median" && " Position médiane — peu différenciée du marché."}
                </div>
              )}
            </div>
          </div>

          {/* Tableau zones grises */}
          {grayList.length > 0 && (
            <div className="noam-card p-4" data-testid="asym-gray-table">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="noam-overline">Priorité — monter en gamme</div>
                  <div className="text-sm font-semibold text-zinc-200">{group.label} en zone grise (top 12 substituables)</div>
                </div>
                <div className="text-[10px] text-zinc-500 inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3 text-[#EF9F27]" /> Convergez vers un centre ou descendez vers une expertise rare
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
                    <tr>
                      <th className="text-left py-1.5 pr-2">Relation</th>
                      <th className="text-left py-1.5 px-2">Source</th>
                      <th className="text-right py-1.5 px-2">Substit.</th>
                      <th className="text-right py-1.5 px-2">Centralité</th>
                      <th className="text-right py-1.5 px-2">Expertise</th>
                      <th className="text-right py-1.5 pl-2">Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grayList.map((it) => (
                      <tr key={it.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`asym-row-${it.id}`}>
                        <td className="py-1.5 pr-2 text-zinc-200 truncate max-w-[260px]">
                          {it.name}
                          {it.subtitle && <span className="text-zinc-500 ml-1">— {it.subtitle.slice(0, 50)}</span>}
                        </td>
                        <td className="py-1.5 px-2 text-zinc-500 uppercase text-[10px]">{it.source}</td>
                        <td className="py-1.5 px-2 text-right text-zinc-400 font-mono">{it.substituability}</td>
                        <td className="py-1.5 px-2 text-right text-[#EF9F27] font-mono">{it.centrality}</td>
                        <td className="py-1.5 px-2 text-right text-[#1D9E75] font-mono">{it.expertise}</td>
                        <td className="py-1.5 pl-2 text-right text-zinc-300 font-mono font-semibold">{it.value_position}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}

      {/* P0 — Modal lecture IA stratégique */}
      {aiReading && (
        <div
          className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          onClick={() => setAiReading("")}
          data-testid="asym-ai-modal"
        >
          <div
            className="noam-card max-w-3xl w-full my-8 p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-full bg-[#EF9F27]/15 border border-[#EF9F27]/30 flex items-center justify-center">
                  <Lightbulb className="w-4 h-4 text-[#EF9F27]" />
                </div>
                <div>
                  <div className="noam-overline">Lecture IA stratégique</div>
                  <h2 className="text-lg font-bold text-zinc-100">
                    Comprendre votre position
                    {aiCategory && aiCategory !== "global" && (
                      <span className="text-zinc-500 text-sm font-normal"> · {aiCategory}</span>
                    )}
                  </h2>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAiReading("")}
                className="text-zinc-400 hover:text-white"
                data-testid="asym-ai-modal-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div
              className="prose prose-invert prose-sm max-w-none text-zinc-200 leading-relaxed whitespace-pre-wrap"
              data-testid="asym-ai-modal-body"
            >
              {aiReading}
            </div>

            {aiSources?.length > 0 && (
              <div className="pt-3 border-t border-white/5">
                <div className="noam-overline mb-1.5">Sources web ({aiSources.length})</div>
                <ul className="space-y-1">
                  {aiSources.slice(0, 6).map((s, i) => (
                    <li key={i} className="text-[11px]">
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-[#378ADD] hover:underline inline-flex items-center gap-1">
                        <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                        <span className="truncate">{s.title || s.url}</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="pt-3 border-t border-white/5 flex items-center justify-between gap-2 flex-wrap">
              <p className="text-[10px] text-zinc-500 italic">
                Lecture générée par NOAM Sonnet 4.5 · contextualisée sur vos données réelles (Annuaire, Pipeline, Courtage, Négociations, Chaînages).
              </p>
              <button
                onClick={() => runAiReading(activeTab)}
                disabled={aiBusy}
                className="btn-ghost text-[11px] disabled:opacity-50"
                data-testid="asym-ai-modal-regen"
              >
                {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                Régénérer
              </button>
            </div>
          </div>
        </div>
      )}
      <DynamicReadingPanel open={dynamicOpen} onClose={() => setDynamicOpen(false)} />
    </div>
  );
}

function Kpi({ label, value, accent, icon: Icon, hint }) {
  return (
    <div className="noam-card p-4">
      <div className="flex items-start justify-between">
        <div className="noam-overline">{label}</div>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent || "#71717A" }} />}
      </div>
      <div className="noam-display text-3xl font-bold mt-1" style={{ color: accent || "#fff" }}>{value}</div>
      {hint && <div className="text-[10px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}
